import { createContext, useContext, useEffect, useState } from 'react'
import { onAuthStateChanged, signOut as fbSignOut, multiFactor } from 'firebase/auth'
import { doc, getDoc, setDoc, onSnapshot, increment, serverTimestamp } from 'firebase/firestore'
import { auth, db } from '../lib/firebase'
import { setClientErrorIdentity } from '../lib/clientErrors'
import { markSignedOut } from '../lib/afterSignIn'

const AuthContext = createContext(null)

const ACTIVE_ORG_KEY = 'incite:activeOrg'
// The platform super-admin(s) — the ONLY identities allowed to view/switch across tenants. This
// MUST be mirrored by a real `platformAdmin` custom claim on the account (set via
// functions/tools/set-platform-admin.mjs) so the SERVER-side tenant guards agree with the client.
const SUPER_ADMIN_EMAILS = ['sean@fit-proof.com']

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)        // Firebase auth user
  const [claims, setClaims] = useState(null)     // { organizationDomain, role }
  const [profile, setProfile] = useState(null)   // userProfiles/{uid} doc
  const [loading, setLoading] = useState(true)
  const [mfaEnrolled, setMfaEnrolled] = useState(false) // has a second factor enrolled?
  // Did THIS SESSION arrive through a federated (SSO) provider? Read from the token's
  // sign_in_provider, never from providerData: providerData lists every provider LINKED to the
  // account, so a user with both a password and Microsoft linked would read as "SSO" even on a
  // password sign-in — and the MFA gate keyed off it would wrongly waive their TOTP.
  const [ssoUser, setSsoUser] = useState(false)
  // Admin/operator-only override: which tenant's data to view. Persisted locally.
  const [activeOrg, setActiveOrgState] = useState(() => {
    try { return localStorage.getItem(ACTIVE_ORG_KEY) } catch { return null }
  })

  useEffect(() => {
    return onAuthStateChanged(auth, async (fbUser) => {
      if (!fbUser) {
        setUser(null)
        setClaims(null)
        setProfile(null)
        setMfaEnrolled(false)
        setSsoUser(false)
        setLoading(false)
        return
      }
      setUser(fbUser)
      // HIPAA: a second factor is required for everyone. Whether this user has one enrolled
      // gates access in App.jsx (unenrolled users are routed to forced setup).
      try { setMfaEnrolled(multiFactor(fbUser).enrolledFactors.length > 0) } catch { setMfaEnrolled(false) }
      // Read custom claims (tenant + role) set server-side on the auth token.
      let token = await fbUser.getIdTokenResult()
      // If this is a designated super-admin whose TOKEN doesn't yet carry the platformAdmin claim
      // (e.g. the claim was just granted server-side, or the token predates it), force ONE token
      // refresh to pull it. Without this, the client would treat them as super-admin (email
      // whitelist) while the SERVER guards see no platformAdmin claim — the exact split that made
      // Sarah reject impersonated-tenant messages as "Organization mismatch".
      const isSuperEmail = SUPER_ADMIN_EMAILS.includes((fbUser.email || '').toLowerCase())
      if (isSuperEmail && token.claims.platformAdmin !== true) {
        try { token = await fbUser.getIdTokenResult(true) } catch { /* keep the token we have */ }
      }
      // `oidc.esgwnd` etc. — our per-tenant Entra providers. A session that came in this way is
      // MFA'd by the customer's own Entra policies; forcing OUR TOTP on top would break the
      // single-sign-on promise (see the App.jsx gate).
      setSsoUser(String(token.claims.firebase?.sign_in_provider || '').startsWith('oidc.'))
      setClaims({
        organizationDomain: token.claims.organizationDomain || null,
        role: token.claims.role || 'user',
        platformAdmin: token.claims.platformAdmin === true,
        // Module grants come from the TOKEN, never from the profile doc: userProfiles is
        // self-writable, so a profile-held list would let any user grant themselves everything.
        // Absent claim = no grants, which is the intended default for a new user.
        modules: Array.isArray(token.claims.modules) ? token.claims.modules : [],
      })
      // Load the user's profile document if present.
      let prof = null
      try {
        const snap = await getDoc(doc(db, 'userProfiles', fbUser.uid))
        prof = snap.exists() ? { id: snap.id, ...snap.data() } : null
        setProfile(prof)
      } catch {
        setProfile(null)
      }
      // Login tracking: lastSignInTime only changes on a real sign-in (not session
      // restore), so count a login whenever it differs from what we last recorded.
      // SEC-9: only write when the profile doc EXISTS — for a brand-new user (no profile yet) this
      // merge is a CREATE, and the rules' create predicate requires organizationDomain (absent from
      // this payload), so the write was silently denied and login tracking never initialized. The
      // bootstrap flow creates the profile with organizationDomain; tracking starts on next sign-in.
      try {
        const lastSignIn = fbUser.metadata?.lastSignInTime || null
        if (prof && lastSignIn && lastSignIn !== prof?.lastRecordedSignIn) {
          await setDoc(doc(db, 'userProfiles', fbUser.uid), {
            loginCount: increment(1),
            lastRecordedSignIn: lastSignIn,
            lastLoginAt: serverTimestamp(),
            ...(prof?.firstJoinedAt ? {} : { firstJoinedAt: fbUser.metadata?.creationTime ? new Date(fbUser.metadata.creationTime) : serverTimestamp() }),
          }, { merge: true })
        }
      } catch (e) { console.warn('login tracking write failed:', e?.message) }   // quiet-by-design: sign-in telemetry; a failed stamp must never block the sign-in
      setLoading(false)
    })
  }, [])

  // HIPAA technical safeguard — automatic sign-out after 30 minutes of inactivity.
  //
  // ACTIVITY IS TRACKED ACROSS TABS, not per tab. The original version listened only to its own
  // window, so a second tab — the check-in kiosk, a drill-down opened with target="_blank", a
  // report left open beside the app — was invisible to it. Working in that tab for half an hour
  // looked like thirty minutes of idleness to the first one, which then called fbSignOut() and
  // killed the session in EVERY tab, because Firebase auth is shared across the origin.
  //
  // The last-activity timestamp goes in localStorage, which is shared by every tab on the origin
  // and fires a `storage` event in the others. Each tab keeps its own timer but they all read the
  // same clock, so the countdown only runs when the PERSON is idle — which is what the safeguard
  // is actually for. A shorter poll (rather than one long timeout) means a tab that was asleep
  // still notices an expiry promptly after waking.
  useEffect(() => {
    if (!user) return undefined
    const IDLE_MS = 30 * 60 * 1000
    const KEY = 'incite.lastActivity'
    const CHECK_MS = 30 * 1000

    const stamp = () => { try { localStorage.setItem(KEY, String(Date.now())) } catch { /* private mode */ } }
    const lastActivity = () => {
      try {
        const v = Number(localStorage.getItem(KEY))
        return Number.isFinite(v) && v > 0 ? v : Date.now()
      } catch { return Date.now() }
    }

    stamp()
    const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'visibilitychange']
    events.forEach((e) => window.addEventListener(e, stamp, { passive: true }))

    const poll = setInterval(() => {
      if (Date.now() - lastActivity() >= IDLE_MS) {
        clearInterval(poll)
        try { localStorage.removeItem(KEY) } catch { /* ignore */ }
        // Deliberately NOT markSignedOut(): after an idle sign-out the person who signs back in is
        // almost always the same one, and returning them to the page they were on is the point of
        // the remembered destination. Pages they cannot use are still gated after sign-in.
        fbSignOut(auth)
      }
    }, CHECK_MS)

    return () => {
      clearInterval(poll)
      events.forEach((e) => window.removeEventListener(e, stamp))
    }
  }, [user])

  // GRANT CHANGES REACH A LIVE SESSION. Custom claims are baked into the ID token, so an operator
  // granting a module mid-session would otherwise be invisible until the token happened to
  // refresh (up to an hour) — the user would keep seeing "ask your administrator" after being
  // given access. The server stamps modulesUpdatedAt on every grant write; watching our OWN
  // profile doc for it and force-refreshing the token closes the gap to about a second.
  // The user's own display timezone override (userProfiles.timezone) — set by an operator for
  // staff working outside the org's zone. Display-only: pages combine it with the ORG zone via
  // effectiveTz(); obligation bucketing never reads it.
  const [userTimezone, setUserTimezone] = useState(null)

  useEffect(() => {
    if (!user) return undefined
    let first = true
    let seen = null
    return onSnapshot(doc(db, 'userProfiles', user.uid), (snap) => {
      setUserTimezone(snap.data()?.timezone || null)
      const stamp = snap.data()?.modulesUpdatedAt
      const key = stamp?.toMillis?.() ?? stamp ?? null
      // The FIRST snapshot is the baseline whatever it holds — including nothing. Bailing out on an
      // absent stamp left `seen` at null, so when the operator's very FIRST grant landed and the
      // stamp appeared, it was mistaken for the baseline and swallowed: the console said "Updated",
      // the user kept seeing "ask your administrator" until they signed out, and the panel's own
      // promise ("appears within a few seconds") was false exactly once per user — on the grant
      // that mattered. Every profile written before module grants existed presents that shape.
      if (first) { first = false; seen = key; return }
      if (key === seen) return
      seen = key
      if (key == null) return // stamp cleared rather than advanced — nothing to refresh toward
      user.getIdTokenResult(true)
        .then((t) => setClaims((c) => ({ ...c, role: t.claims.role || 'user', modules: Array.isArray(t.claims.modules) ? t.claims.modules : [] })))
        .catch(() => {})
    }, () => {})
  }, [user])

  // The platform super-admin — the ONLY identity allowed to view/switch across tenants. Prefer the
  // real custom claim (which the server also trusts); the email whitelist is only a fallback while a
  // super-admin's token is catching up to a just-granted claim (see the force-refresh above).
  const platformAdmin = claims?.platformAdmin === true || SUPER_ADMIN_EMAILS.includes((user?.email || '').toLowerCase())
  const modules = claims?.modules || []
  // A per-org "admin" role — elevated WITHIN its own tenant only; never cross-tenant.
  const isAdmin = claims?.role === 'admin' || platformAdmin
  const claimOrg = claims?.organizationDomain || null
  // SECURITY: only the platform super-admin can ever view another tenant. Every other
  // user (including org `role:'admin'`) is hard-locked to their own claim's tenant.
  const effectiveOrg = platformAdmin && activeOrg ? activeOrg : claimOrg

  // Keep crash telemetry stamped with the caller's own tenant (rules require it on create)
  // and, for a platform admin, which tenant they were viewing when it crashed.
  useEffect(() => {
    setClientErrorIdentity({ domain: claimOrg, viewingDomain: effectiveOrg })
  }, [claimOrg, effectiveOrg])

  const setActiveOrg = (domain) => {
    if (!platformAdmin) return // non-super-admins can never override their tenant
    const next = domain && domain !== claimOrg ? domain : null
    setActiveOrgState(next)
    try { next ? localStorage.setItem(ACTIVE_ORG_KEY, next) : localStorage.removeItem(ACTIVE_ORG_KEY) } catch { /* ignore */ }
  }

  const value = {
    user,
    claims,
    profile,
    loading,
    mfaEnrolled, ssoUser,
    organizationDomain: effectiveOrg,      // effective tenant used by all data hooks
    claimOrg,                              // the caller's own tenant (from the claim)
    isAdmin,
    // Named "superuser" in the product: the org's own top user. Same claim as isAdmin — kept as
    // one value so the two can never drift apart — but exposed under the name the UI uses.
    isSuperuser: isAdmin,
    modules,
    userTimezone,                          // per-user display override, null = follow the org
    platformAdmin,
    activeOrg: platformAdmin ? activeOrg : null,
    isImpersonating: platformAdmin && !!activeOrg && activeOrg !== claimOrg,
    setActiveOrg,
    // Forget any remembered destination first, so the next sign-in in this tab is not sent to the
    // page this person signed out of (src/lib/afterSignIn.js).
    signOut: () => { markSignedOut(); return fbSignOut(auth) },
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>')
  return ctx
}
